import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { queryAggregateNotification } from '@itp/prisma-talent/gql';
import { usePrivateApollo, useSocket, useUser } from '@itp/talent';

interface NotificationCountContextType {
  refetchUnread: () => Promise<void>;
  unread: number;
}

const NotificationCountContext = createContext<NotificationCountContextType | undefined>(undefined);

export const NotificationCountProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useUser();
  const socket = useSocket();
  const [unread, setUnread] = useState(0);

  const { executeQuery } = usePrivateApollo<{ _count: { id: number } }>({
    onCompleted: ({ data }) => {
      setUnread(data?._count.id ?? 0);
    },
    query: queryAggregateNotification,
    showErrors: false,
    skip: !user?.id && !user.isAdmin,
    variables: {
      where: { readAt: { equals: null }, toId: { equals: user?.id } },
    },
  });

  const refetchUnread = useCallback(async () => {
    const { data } = await executeQuery();
    setUnread(data?.data?._count.id ?? 0);
  }, [executeQuery]);

  useEffect(() => {
    if (socket) {
      socket.on(['notification-created', 'notification-updated'], refetchUnread);
      return () => {
        socket.off(['notification-created', 'notification-updated']);
      };
    }
  }, [refetchUnread, socket]);

  return (
    <NotificationCountContext.Provider value={{ refetchUnread, unread }}>
      {children}
    </NotificationCountContext.Provider>
  );
};

export const useNotificationCount = () => {
  const context = useContext(NotificationCountContext);
  if (!context) {
    throw new Error('useNotificationCount must be used within a NotificationCountProvider');
  }
  return context;
};
